import React from 'react';
import type { DailyCompsetDay } from '@app/model/bi/Bi';
import { useDailyDayPopup } from '@app/model/bi/service/BiHooks';
import { scanSource, type CompsetChoice } from './DailyPerformance.utilities';
import { formatDateTime } from '../Bi.utilities';

// ROO2-289 (G8, D18, D19): the card of the vs Median chip. On a wide screen it is the RoomPulse day popup of the night: the
// HTML of the embed card (B5), shown in a sandboxed frame with no script; its "Open in RoomPulse" opens the RateShopper. On a
// phone (compact) the card holds the figures of the night: the own price, the compset value, the difference and the scan time.
type Props = { companyHotelId: number, night: string, label: string, day: DailyCompsetDay | undefined, choice: CompsetChoice, nights: number, currency: string, compact: boolean };

const money = (v: number | null | undefined, currency: string) => v === null || v === undefined ? '–'
	: v.toLocaleString('en-GB', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signed = (v: number | null | undefined) => v === null || v === undefined ? '–' : `${v > 0 ? '+' : ''}${v} %`;

// ROO2-301 W1: the popup shows the line of the selected compset value (choice) only.
const DayPopup: React.FunctionComponent<{ companyHotelId: number, night: string, label: string, choice: CompsetChoice }> = ({ companyHotelId, night, label, choice }) => {
	const popup = useDailyDayPopup(companyHotelId, night, choice);
	// The card is 850 px wide; at 90 % it fits a 767 px frame, and the frame scrolls a long competitor list.
	return <div className="w-[767px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-gray-900/10">
		{popup.data === undefined
			? <p className="p-4 text-sm text-gray-500">{popup.isError ? `The day popup of ${label} did not load. Try again later.` : `Loading the day popup of ${label}…`}</p>
			: <div className="h-[522px] overflow-hidden">
				<iframe title={`RoomPulse day popup of ${label}`} srcDoc={popup.data}
					sandbox="allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
					className="h-[580px] w-[850px] origin-top-left scale-90 border-0" />
			</div>}
	</div>;
};

const DailyDayCard: React.FunctionComponent<Props> = ({ companyHotelId, night, label, day, choice, nights, currency, compact }) => {
	if (!compact) return <DayPopup companyHotelId={companyHotelId} night={night} label={label} choice={choice} />;
	const value = choice === 'WEIGHTED_AVERAGE' ? day?.weightedAverage : day?.median;
	return <div className="w-72 max-w-[calc(100vw-2rem)] rounded-lg bg-white p-4 text-xs text-gray-700 shadow-xl ring-1 ring-gray-900/10">
		<p className="text-sm font-semibold text-gray-900">{label}</p>
		<dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
			<dt className="text-gray-500">Your lowest price</dt><dd className="text-right font-medium tabular-nums text-gray-900">{money(day?.ownPrice, currency)}</dd>
			<dt className="text-gray-500">Compset {choice === 'WEIGHTED_AVERAGE' ? 'W. avg' : 'median'}</dt><dd className="text-right font-medium tabular-nums text-gray-900">{money(value?.price, currency)}</dd>
			<dt className="text-gray-500">Difference</dt><dd className="text-right font-medium tabular-nums text-gray-900">{signed(value?.difference)}</dd>
		</dl>
		<p className="mt-2 text-gray-500">{scanSource(nights)}.
			{day?.updatedAt === null || day?.updatedAt === undefined ? '' : ` Scan ${formatDateTime(day.updatedAt)} UTC.`}</p>
	</div>;
};

export default DailyDayCard;
